import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Home, Users, Bath, Dog, DoorOpen, Sparkles,
  Plus, Minus, Info, AlertTriangle, Check,
  ChevronRight, ExternalLink, Calendar, Clock, Save, Eye,
  DollarSign, Percent, X, Edit2, MapPin
} from 'lucide-react';
import {
  ADDONS, ROOM_SIZES,
  generateEstimate, formatPrice, calculateAllFrequencies
} from '../utils/pricingCalculator';
import { homeConditions, cleaningFrequencies } from '../utils/estimateData';
import { OFFICE } from '../utils/jobData';
import './EstimateCalculator.css';

// Haversine distance in miles between two {lat, lng} points
function milesBetween(a, b) {
  if (!a || !b || a.lat == null || a.lng == null || b.lat == null || b.lng == null) return null;
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

// Home condition images (placeholders - replace with real images)
const conditionImages = {
  super_clean: '✨',
  second_home: '🏠',
  lived_in: '👨‍👩‍👧',
  busy_home: '👨‍👩‍👧‍👦',
  overwhelmed: '🆘'
};

function EstimateCalculator({ 
  initialValues = {}, 
  onSaveDraft,
  onSaveAndPreview,
  propertyData = null 
}) {
  // Form state
  const [inputs, setInputs] = useState({
    sqft: propertyData?.sqft || initialValues.sqft || '',
    condition: initialValues.condition || '',
    bedrooms: propertyData?.bedrooms || initialValues.bedrooms || 3,
    fullBaths: Math.floor(propertyData?.bathrooms || 0) || initialValues.fullBaths || 2,
    halfBaths: ((propertyData?.bathrooms || 0) % 1 > 0 ? 1 : 0) || initialValues.halfBaths || 0,
    people: initialValues.people || 3,
    sheddingPets: initialValues.sheddingPets || 0,
    glassShowerDoors: initialValues.glassShowerDoors || 0,
    excludedRooms: initialValues.excludedRooms || [],
    // Date/Time preferences
    preferredDate: initialValues.preferredDate || '',
    preferredTime: initialValues.preferredTime || '',
    // Notes
    internalNotes: initialValues.internalNotes || ''
  });

  // Multiple frequency selection
  const [selectedFrequencies, setSelectedFrequencies] = useState(
    initialValues.selectedFrequencies || []
  );

  // Per-frequency notes (visible to client on portal)
  const [frequencyNotes, setFrequencyNotes] = useState(
    initialValues.frequencyNotes || {}
  );

  // Price overrides and discounts
  const [priceOverrides, setPriceOverrides] = useState(
    initialValues.priceOverrides || {}
  );
  
  // Line item client-facing descriptions
  const [lineItemDescriptions, setLineItemDescriptions] = useState(
    initialValues.lineItemDescriptions || {}
  );
  const [discounts, setDiscounts] = useState(
    initialValues.discounts || { recurring: null, deepClean: null }
  );
  const [skipDeepClean, setSkipDeepClean] = useState(
    initialValues.skipDeepClean || false
  );
  const [showPricingControls, setShowPricingControls] = useState(false);

  const [selectedAddons, setSelectedAddons] = useState(initialValues.addons || []);
  const [showRoomExclusions, setShowRoomExclusions] = useState(false);
  const [activeAddonCategory, setActiveAddonCategory] = useState('appliances');

  // Update input handler
  const updateInput = useCallback((field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  }, []);

  // Toggle frequency selection
  const toggleFrequency = (freq) => {
    setSelectedFrequencies(prev => {
      if (prev.includes(freq)) {
        return prev.filter(f => f !== freq);
      }
      return [...prev, freq];
    });
  };

  // Number input with increment/decrement
  const NumberInput = ({ label, field, min = 0, max = 20, icon: Icon }) => (
    <div className="number-input-group">
      <label>
        {Icon && <Icon size={16} />}
        {label}
      </label>
      <div className="number-controls">
        <button 
          type="button"
          onClick={() => updateInput(field, Math.max(min, inputs[field] - 1))}
          disabled={inputs[field] <= min}
        >
          <Minus size={16} />
        </button>
        <span className="number-value">{inputs[field]}</span>
        <button 
          type="button"
          onClick={() => updateInput(field, Math.min(max, inputs[field] + 1))}
          disabled={inputs[field] >= max}
        >
          <Plus size={16} />
        </button>
      </div>
    </div>
  );

  // Toggle addon selection
  const toggleAddon = (addonId, quantity = 1) => {
    setSelectedAddons(prev => {
      const existing = prev.find(a => a.addonId === addonId);
      if (existing) {
        return prev.filter(a => a.addonId !== addonId);
      }
      return [...prev, { addonId, quantity }];
    });
  };

  // Update addon quantity
  const updateAddonQuantity = (addonId, quantity) => {
    setSelectedAddons(prev => 
      prev.map(a => a.addonId === addonId ? { ...a, quantity: Math.max(1, quantity) } : a)
    );
  };

  // Check if addon is selected
  const isAddonSelected = (addonId) => {
    return selectedAddons.some(a => a.addonId === addonId);
  };

  // Get addon quantity
  const getAddonQuantity = (addonId) => {
    return selectedAddons.find(a => a.addonId === addonId)?.quantity || 1;
  };

  // Toggle room exclusion
  const toggleRoomExclusion = (room) => {
    setInputs(prev => ({
      ...prev,
      excludedRooms: prev.excludedRooms.includes(room)
        ? prev.excludedRooms.filter(r => r !== room)
        : [...prev.excludedRooms, room]
    }));
  };

  // Price override handlers
  const setRecurringOverride = (freq, price) => {
    setPriceOverrides(prev => ({
      ...prev,
      [freq]: price === '' ? null : parseFloat(price)
    }));
  };

  const setDeepCleanOverride = (price) => {
    setPriceOverrides(prev => ({
      ...prev,
      deepClean: price === '' ? null : parseFloat(price)
    }));
  };

  const setDiscount = (type, value) => {
    setDiscounts(prev => ({
      ...prev,
      [type]: value === '' ? null : { type: 'flat', amount: parseFloat(value) }
    }));
  };

  const setPercentDiscount = (type, value) => {
    setDiscounts(prev => ({
      ...prev,
      [type]: value === '' ? null : { type: 'percent', amount: parseFloat(value) }
    }));
  };

  // Apply discount to a price
  const applyDiscount = (price, discount) => {
    if (!discount) return price;
    if (discount.type === 'percent') {
      return price * (1 - discount.amount / 100);
    }
    return Math.max(0, price - discount.amount);
  };

  // Calculate estimates for all selected frequencies
  const estimates = useMemo(() => {
    if (!inputs.sqft || !inputs.condition || selectedFrequencies.length === 0) {
      return null;
    }

    const results = {};
    selectedFrequencies.forEach(freq => {
      try {
        const baseEstimate = generateEstimate({
          sqft: parseInt(inputs.sqft),
          condition: inputs.condition,
          frequency: freq,
          bedrooms: inputs.bedrooms,
          fullBaths: inputs.fullBaths,
          halfBaths: inputs.halfBaths,
          people: inputs.people,
          sheddingPets: inputs.sheddingPets,
          glassShowerDoors: inputs.glassShowerDoors,
          excludedRooms: inputs.excludedRooms
        }, selectedAddons);

        // Apply recurring price override
        if (priceOverrides[freq] !== undefined && priceOverrides[freq] !== null) {
          baseEstimate.total = priceOverrides[freq];
          baseEstimate.priceOverride = true;
        }

        // Apply recurring discount
        if (discounts.recurring) {
          const originalTotal = baseEstimate.total;
          baseEstimate.total = applyDiscount(baseEstimate.total, discounts.recurring);
          baseEstimate.discountApplied = {
            ...discounts.recurring,
            savedAmount: originalTotal - baseEstimate.total
          };
        }

        // Apply deep clean price override
        if (baseEstimate.deepCleanEstimate) {
          if (priceOverrides.deepClean !== undefined && priceOverrides.deepClean !== null) {
            baseEstimate.deepCleanEstimate.subtotal = priceOverrides.deepClean;
            baseEstimate.deepCleanEstimate.priceOverride = true;
          }

          // Apply deep clean discount
          if (discounts.deepClean) {
            const originalDeepClean = baseEstimate.deepCleanEstimate.subtotal;
            baseEstimate.deepCleanEstimate.subtotal = applyDiscount(
              baseEstimate.deepCleanEstimate.subtotal, 
              discounts.deepClean
            );
            baseEstimate.deepCleanEstimate.discountApplied = {
              ...discounts.deepClean,
              savedAmount: originalDeepClean - baseEstimate.deepCleanEstimate.subtotal
            };
          }
        }

        // Handle skip deep clean
        baseEstimate.skipDeepClean = skipDeepClean;

        // Addon line items are already merged into deepCleanEstimate by generateEstimate()
        // in pricingCalculator.js - no need to merge again here

        results[freq] = baseEstimate;
      } catch (error) {
        console.error(`Calculation error for ${freq}:`, error);
      }
    });

    return results;
  }, [inputs, selectedFrequencies, selectedAddons, priceOverrides, discounts, skipDeepClean]);

  // Calculate all frequency options for comparison
  const allFrequencies = useMemo(() => {
    if (!inputs.sqft || !inputs.condition) return null;
    
    return calculateAllFrequencies({
      sqft: parseInt(inputs.sqft),
      condition: inputs.condition,
      frequency: 'weekly',
      bedrooms: inputs.bedrooms,
      fullBaths: inputs.fullBaths,
      halfBaths: inputs.halfBaths,
      people: inputs.people,
      sheddingPets: inputs.sheddingPets,
      glassShowerDoors: inputs.glassShowerDoors,
      excludedRooms: inputs.excludedRooms
    });
  }, [inputs]);

  // Distance from office (uses property coordinates if available)
  const distanceFromOffice = useMemo(() => {
    const coords = propertyData?.coordinates
      || (propertyData?.lat != null && propertyData?.lng != null
        ? { lat: propertyData.lat, lng: propertyData.lng }
        : null);
    return milesBetween(OFFICE, coords);
  }, [propertyData]);

  const distanceLevel = distanceFromOffice == null
    ? null
    : distanceFromOffice > 20 ? 'danger'
      : distanceFromOffice > 10 ? 'warning'
        : 'ok';

  // Check if any selected frequency is Airbnb
  const hasAirbnb = selectedFrequencies.includes('airbnb');
  
  // Check if review required
  const requiresReview = inputs.condition === 'overwhelmed' || 
    selectedAddons.some(a => ADDONS[a.addonId]?.requiresReview);

  // Can save?
  const canSave = inputs.sqft && inputs.condition && selectedFrequencies.length > 0;

  // Handle save
  const handleSave = (andPreview = false) => {
    const data = {
      inputs,
      selectedFrequencies,
      selectedAddons,
      estimates,
      requiresReview,
      priceOverrides,
      discounts,
      skipDeepClean,
      lineItemDescriptions,
      frequencyNotes
    };
    
    if (andPreview && onSaveAndPreview) {
      onSaveAndPreview(data);
    } else if (onSaveDraft) {
      onSaveDraft(data);
    }
  };

  // Group addons by category
  const addonCategories = {
    appliances: { label: 'Appliances', addons: [] },
    windows: { label: 'Windows & Blinds', addons: [] },
    services: { label: 'Services', addons: [] },
    custom: { label: 'Custom Quote Items', addons: [] }
  };

  Object.entries(ADDONS).forEach(([id, addon]) => {
    if (addonCategories[addon.category]) {
      addonCategories[addon.category].addons.push({ id, ...addon });
    }
  });

  return (
    <div className="estimate-calculator">
      {distanceFromOffice != null && (
        <div className={`distance-banner distance-${distanceLevel}`}>
          <MapPin size={18} />
          <div className="distance-banner-text">
            <strong>{distanceFromOffice.toFixed(1)} mi</strong> from office
            {distanceLevel === 'danger' && (
              <span className="distance-banner-note">
                Over 20 mi — significant travel; review before sending
              </span>
            )}
            {distanceLevel === 'warning' && (
              <span className="distance-banner-note">
                Over 10 mi — outside typical service radius
              </span>
            )}
          </div>
        </div>
      )}

      {/* Step 1: Property Basics */}
      <section className="calc-section">
        <div className="section-header">
          <span className="step-number">1</span>
          <div>
            <h3>Property Details</h3>
            <p>Enter the basic information about the home</p>
          </div>
        </div>

        <div className="form-grid">
          <div className="form-group sqft-input">
            <label>
              <Home size={16} />
              Square Footage
            </label>
            <input
              type="number"
              value={inputs.sqft}
              onChange={(e) => updateInput('sqft', e.target.value)}
              placeholder="e.g., 2200"
              min="500"
              max="15000"
            />
            <span className="input-hint">Verify via Zillow</span>
          </div>

          <NumberInput label="Bedrooms" field="bedrooms" icon={DoorOpen} min={1} max={10} />
          <NumberInput label="Full Baths" field="fullBaths" icon={Bath} min={1} max={10} />
          <NumberInput label="Half Baths" field="halfBaths" icon={Bath} min={0} max={5} />
          <NumberInput label="People in Home" field="people" icon={Users} min={1} max={15} />
          <NumberInput label="Shedding Pets" field="sheddingPets" icon={Dog} min={0} max={10} />
          <NumberInput label="Glass Shower Doors" field="glassShowerDoors" icon={Sparkles} min={0} max={10} />
        </div>

        {/* Room Exclusions */}
        <div className="room-exclusions">
          <button 
            type="button"
            className="exclusion-toggle"
            onClick={() => setShowRoomExclusions(!showRoomExclusions)}
          >
            <span>Exclude rooms from cleaning?</span>
            <ChevronRight 
              size={18} 
              className={showRoomExclusions ? 'rotated' : ''} 
            />
          </button>
          
          {showRoomExclusions && (
            <div className="exclusion-grid">
              {Object.entries(ROOM_SIZES).map(([room, sqft]) => (
                <label key={room} className="exclusion-item">
                  <input
                    type="checkbox"
                    checked={inputs.excludedRooms.includes(room)}
                    onChange={() => toggleRoomExclusion(room)}
                  />
                  <span className="room-name">
                    {room.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                  <span className="room-sqft">~{sqft} sqft</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Step 2: Home Condition */}
      <section className="calc-section">
        <div className="section-header">
          <span className="step-number">2</span>
          <div>
            <h3>Home Condition</h3>
            <p>Select the image that best describes the home</p>
          </div>
        </div>

        <div className="condition-grid">
          {Object.entries(homeConditions).map(([key, config]) => (
            <button
              key={key}
              type="button"
              className={`condition-card ${inputs.condition === key ? 'selected' : ''} ${key === 'overwhelmed' ? 'warning' : ''}`}
              onClick={() => updateInput('condition', key)}
            >
              <span className="condition-emoji">{conditionImages[key]}</span>
              <span className="condition-label">{config.label}</span>
              <span className="condition-desc">{config.description}</span>
              {inputs.condition === key && (
                <span className="check-badge"><Check size={16} /></span>
              )}
            </button>
          ))}
        </div>

        {inputs.condition === 'overwhelmed' && (
          <div className="warning-banner">
            <AlertTriangle size={18} />
            <span>This selection requires manager review before sending</span>
          </div>
        )}
      </section>

      {/* Step 3: Service Frequencies (Multi-select) */}
      <section className="calc-section">
        <div className="section-header">
          <span className="step-number">3</span>
          <div>
            <h3>Service Options</h3>
            <p>Select ALL frequencies to include in this estimate (client can choose)</p>
          </div>
        </div>

        <div className="frequency-grid multi-select">
          {Object.entries(cleaningFrequencies)
            .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
            .map(([key, config]) => {
              const priceInfo = allFrequencies?.[key === 'one_time' ? 'deep_clean' : key];
              const isSelected = selectedFrequencies.includes(key);
              
              return (
                <button
                  key={key}
                  type="button"
                  className={`frequency-card ${isSelected ? 'selected' : ''} ${config.requiresCustomQuote ? 'custom-quote' : ''}`}
                  onClick={() => toggleFrequency(key)}
                >
                  <div className="freq-checkbox">
                    {isSelected && <Check size={14} />}
                  </div>
                  <span className="frequency-label">{config.label}</span>
                  {priceInfo && !config.requiresCustomQuote ? (
                    <span className="frequency-price">
                      {formatPrice(priceInfo.subtotal)}
                      {priceInfo.minimumApplied && <span className="min-badge">min</span>}
                    </span>
                  ) : config.requiresCustomQuote ? (
                    <span className="frequency-price custom">Custom Quote</span>
                  ) : null}
                </button>
              );
            })}
        </div>

        {selectedFrequencies.length > 0 && (
          <div className="selected-frequencies">
            <strong>Selected:</strong> {selectedFrequencies.map(f => cleaningFrequencies[f]?.label).join(', ')}
          </div>
        )}

        {hasAirbnb && (
          <div className="info-banner">
            <Info size={18} />
            <span>Short-term rental turnovers require a custom quote based on property manager rates</span>
          </div>
        )}

        {/* Per-frequency notes (shown when frequencies are selected) */}
        {selectedFrequencies.length > 0 && (
          <div className="frequency-notes-section" style={{ marginTop: '16px' }}>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '8px' }}>
              Add notes per frequency (visible to client on their estimate)
            </p>
            {selectedFrequencies.map(freq => (
              <div key={freq} style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', marginBottom: '4px' }}>
                  {cleaningFrequencies[freq]?.label || freq}
                </label>
                <textarea
                  className="notes-textarea"
                  value={frequencyNotes[freq] || ''}
                  onChange={(e) => setFrequencyNotes(prev => ({ ...prev, [freq]: e.target.value }))}
                  placeholder={`Notes for ${cleaningFrequencies[freq]?.label || freq} service...`}
                  rows={2}
                  style={{ fontSize: '0.85rem' }}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Step 4: Date/Time Preferences */}
      <section className="calc-section">
        <div className="section-header">
          <span className="step-number">4</span>
          <div>
            <h3>Client Preferences</h3>
            <p>When would the client prefer service?</p>
          </div>
        </div>

        <div className="preferences-grid">
          <div className="form-group">
            <label>
              <Calendar size={16} />
              Preferred Start Date
            </label>
            <input
              type="date"
              value={inputs.preferredDate}
              onChange={(e) => updateInput('preferredDate', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>
              <Clock size={16} />
              Preferred Time
            </label>
            <select
              value={inputs.preferredTime}
              onChange={(e) => updateInput('preferredTime', e.target.value)}
            >
              <option value="">No preference</option>
              <option value="morning">Morning (8am - 12pm)</option>
              <option value="afternoon">Afternoon (12pm - 4pm)</option>
              <option value="anytime">Anytime</option>
            </select>
          </div>
        </div>
      </section>

      {/* Step 5: Add-Ons */}
      <section className="calc-section">
        <div className="section-header">
          <span className="step-number">5</span>
          <div>
            <h3>Add-On Services</h3>
            <p>Select any additional services for this estimate</p>
          </div>
        </div>

        <div className="addon-tabs">
          {Object.entries(addonCategories).map(([key, category]) => (
            <button
              key={key}
              type="button"
              className={`addon-tab ${activeAddonCategory === key ? 'active' : ''}`}
              onClick={() => setActiveAddonCategory(key)}
            >
              {category.label}
              {selectedAddons.filter(a => ADDONS[a.addonId]?.category === key).length > 0 && (
                <span className="addon-count">
                  {selectedAddons.filter(a => ADDONS[a.addonId]?.category === key).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="addon-list">
          {addonCategories[activeAddonCategory].addons.map(addon => {
            const isSelected = isAddonSelected(addon.id);
            const quantity = getAddonQuantity(addon.id);
            const isQuantifiable = addon.unit !== 'flat' && addon.unit !== 'custom';
            
            // Special label for organizing
            const displayLabel = addon.id === 'organizing' 
              ? 'Organizing Services - Hourly (2 hour minimum)'
              : addon.label;
            
            return (
              <div 
                key={addon.id} 
                className={`addon-item ${isSelected ? 'selected' : ''} ${addon.requiresReview ? 'requires-review' : ''}`}
              >
                <div className="addon-main" onClick={() => toggleAddon(addon.id)}>
                  <div className="addon-checkbox">
                    {isSelected && <Check size={14} />}
                  </div>
                  <div className="addon-info">
                    <span className="addon-name">{displayLabel}</span>
                    {addon.requiresPhoto && (
                      <span className="addon-note">📷 Photo required</span>
                    )}
                  </div>
                  <div className="addon-price">
                    {addon.price !== null ? (
                      <>
                        {formatPrice(addon.price)}
                        {addon.unit !== 'flat' && <span className="price-unit">/{addon.unit.replace('per ', '')}</span>}
                      </>
                    ) : addon.externalLink ? (
                      <a 
                        href={addon.externalLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="external-link"
                      >
                        Open Form <ExternalLink size={14} />
                      </a>
                    ) : (
                      <span className="custom-quote">Custom Quote</span>
                    )}
                  </div>
                </div>
                
                {isSelected && isQuantifiable && (
                  <div className="addon-quantity">
                    <span>{addon.id === 'organizing' ? 'Hours (2 min):' : 'Quantity:'}</span>
                    <div className="quantity-controls">
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const minQty = addon.id === 'organizing' ? 2 : 1;
                          updateAddonQuantity(addon.id, Math.max(minQty, quantity - 1));
                        }}
                      >
                        <Minus size={14} />
                      </button>
                      <input
                        type="number"
                        value={quantity}
                        onChange={(e) => {
                          const minQty = addon.id === 'organizing' ? 2 : 1;
                          updateAddonQuantity(addon.id, Math.max(minQty, parseInt(e.target.value) || minQty));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        min={addon.id === 'organizing' ? 2 : 1}
                      />
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateAddonQuantity(addon.id, quantity + 1);
                        }}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <span className="quantity-total">
                      = {formatPrice(addon.price * quantity)}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Step 6: Notes */}
      <section className="calc-section">
        <div className="section-header">
          <span className="step-number">6</span>
          <div>
            <h3>Internal Notes</h3>
            <p>Add any notes about this estimate (not visible to client)</p>
          </div>
        </div>

        <textarea
          className="notes-textarea"
          value={inputs.internalNotes}
          onChange={(e) => updateInput('internalNotes', e.target.value)}
          placeholder="E.g., Client mentioned they have a large dog, prefers same cleaner each visit..."
          rows={4}
        />
      </section>

      {/* Step 7: Pricing Adjustments */}
      {estimates && Object.keys(estimates).length > 0 && (
        <section className="calc-section pricing-section">
          <div className="section-header">
            <span className="step-number">7</span>
            <div>
              <h3>Pricing Adjustments</h3>
              <p>Override calculated prices or add discounts</p>
            </div>
            <button 
              type="button"
              className={`btn btn-sm ${showPricingControls ? 'btn-secondary' : 'btn-outline'}`}
              onClick={() => setShowPricingControls(!showPricingControls)}
            >
              <Edit2 size={16} />
              {showPricingControls ? 'Hide' : 'Show'} Controls
            </button>
          </div>

          {showPricingControls && (
            <div className="pricing-controls">
              {/* Skip Deep Clean Option */}
              <div className="pricing-option skip-deep-clean">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={skipDeepClean}
                    onChange={(e) => setSkipDeepClean(e.target.checked)}
                  />
                  <span className="checkbox-custom">
                    {skipDeepClean && <Check size={14} />}
                  </span>
                  <div className="option-text">
                    <strong>Skip Deep Clean</strong>
                    <span>Client doesn't need deep clean (e.g., existing client, already clean home)</span>
                  </div>
                </label>
              </div>

              {/* Deep Clean Price Override */}
              {!skipDeepClean && (
                <div className="pricing-row">
                  <div className="pricing-label">
                    <Sparkles size={16} />
                    <span>Deep Clean Price</span>
                  </div>
                  <div className="pricing-inputs">
                    <div className="input-group">
                      <label>Override ($)</label>
                      <div className="currency-input">
                        <DollarSign size={16} />
                        <input
                          type="number"
                          step="5"
                          min="0"
                          placeholder={Object.values(estimates)[0]?.deepCleanEstimate?.subtotal || ''}
                          value={priceOverrides.deepClean ?? ''}
                          onChange={(e) => setDeepCleanOverride(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="input-group">
                      <label>Or Discount ($)</label>
                      <div className="currency-input">
                        <Minus size={16} />
                        <input
                          type="number"
                          step="5"
                          min="0"
                          placeholder="0"
                          value={discounts.deepClean?.type === 'flat' ? discounts.deepClean.amount : ''}
                          onChange={(e) => setDiscount('deepClean', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="input-group">
                      <label>Or Discount (%)</label>
                      <div className="percent-input">
                        <input
                          type="number"
                          step="5"
                          min="0"
                          max="100"
                          placeholder="0"
                          value={discounts.deepClean?.type === 'percent' ? discounts.deepClean.amount : ''}
                          onChange={(e) => setPercentDiscount('deepClean', e.target.value)}
                        />
                        <Percent size={16} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recurring Price Overrides */}
              <div className="pricing-row">
                <div className="pricing-label">
                  <Calendar size={16} />
                  <span>Recurring Price</span>
                </div>
                <div className="pricing-inputs">
                  <div className="input-group">
                    <label>Override ($)</label>
                    <div className="override-per-frequency">
                      {selectedFrequencies.map(freq => (
                        <div key={freq} className="frequency-override">
                          <span className="freq-label">{cleaningFrequencies[freq]?.label}</span>
                          <div className="currency-input small">
                            <DollarSign size={14} />
                            <input
                              type="number"
                              step="5"
                              min="0"
                              placeholder={estimates[freq]?.total ? Math.round(estimates[freq]?.total) : ''}
                              value={priceOverrides[freq] ?? ''}
                              onChange={(e) => setRecurringOverride(freq, e.target.value)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="input-group">
                    <label>Or Discount ($ off all)</label>
                    <div className="currency-input">
                      <Minus size={16} />
                      <input
                        type="number"
                        step="5"
                        min="0"
                        placeholder="0"
                        value={discounts.recurring?.type === 'flat' ? discounts.recurring.amount : ''}
                        onChange={(e) => setDiscount('recurring', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="input-group">
                    <label>Or Discount (%)</label>
                    <div className="percent-input">
                      <input
                        type="number"
                        step="5"
                        min="0"
                        max="100"
                        placeholder="0"
                        value={discounts.recurring?.type === 'percent' ? discounts.recurring.amount : ''}
                        onChange={(e) => setPercentDiscount('recurring', e.target.value)}
                      />
                      <Percent size={16} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Clear All Button */}
              <div className="pricing-actions">
                <button 
                  type="button" 
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    setPriceOverrides({});
                    setDiscounts({ recurring: null, deepClean: null });
                    setSkipDeepClean(false);
                  }}
                >
                  <X size={14} />
                  Clear All Adjustments
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Line Item Descriptions (Client-facing) */}
      {estimates && Object.keys(estimates).length > 0 && (() => {
        const firstEstimate = Object.values(estimates)[0];
        const deepCleanItems = firstEstimate?.deepCleanEstimate?.lineItems || [];
        // For one-time/deep clean frequencies, line items are in the main estimate (no deepCleanEstimate)
        const isDeepCleanFreq = selectedFrequencies.some(f => ['one_time', 'deep_clean', 'move_in', 'move_out', 'move_in_out'].includes(f));
        const mainItems = isDeepCleanFreq && deepCleanItems.length === 0 ? (firstEstimate?.lineItems || []) : [];
        const allItems = [...deepCleanItems, ...mainItems];
        if (allItems.length === 0) return null;
        return (
          <section className="calc-section">
            <div className="section-header">
              <span className="step-number">✏️</span>
              <div>
                <h3>Service Line Item Descriptions</h3>
                <p>Add client-facing descriptions for line items (visible on their estimate)</p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {allItems.map((item) => (
                <div key={`dc-${item.id}`} style={{ background: '#fefce8', borderRadius: '8px', padding: '12px 16px', border: '1px solid #fde68a' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>🧹 {item.label}</span>
                    <span style={{ color: '#2c5f4e', fontWeight: 600 }}>{item.price !== null ? `$${item.price.toFixed(2)}` : 'TBD'}</span>
                  </div>
                  <input
                    type="text"
                    placeholder={`Description for client`}
                    value={lineItemDescriptions[`dc_${item.id}`] || ''}
                    onChange={(e) => setLineItemDescriptions(prev => ({ ...prev, [`dc_${item.id}`]: e.target.value }))}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', color: '#475569' }}
                  />
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      {/* Estimate Summary */}
      {estimates && Object.keys(estimates).length > 0 && (
        <section className="calc-section summary-section">
          <div className="section-header">
            <span className="step-number">✓</span>
            <div>
              <h3>Estimate Summary</h3>
              <p>Review the calculated pricing for selected services</p>
            </div>
          </div>

          <div className="summary-card">
            {/* Deep Clean Summary (if not skipped) */}
            {!skipDeepClean && Object.values(estimates)[0]?.deepCleanEstimate && (
              <div className="summary-block deep-clean-block">
                <h4>
                  <Sparkles size={16} />
                  Deep Clean
                </h4>
                <div className="line-items">
                  {Object.values(estimates)[0].deepCleanEstimate.lineItems?.map((item, idx) => (
                    <div key={idx} className={`line-item ${item.type}`}>
                      <span className="item-label">{item.label}</span>
                      <span className="item-desc">{item.description}</span>
                      <span className="item-price">
                        {item.price !== null ? formatPrice(item.price) : 'TBD'}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="subtotal">
                  <span>
                    Deep Clean Total
                    {Object.values(estimates)[0].deepCleanEstimate.priceOverride && (
                      <span className="override-badge">Override</span>
                    )}
                  </span>
                  <div className="price-with-discount">
                    {Object.values(estimates)[0].deepCleanEstimate.discountApplied && (
                      <span className="original-price">
                        {formatPrice(
                          Object.values(estimates)[0].deepCleanEstimate.subtotal + 
                          Object.values(estimates)[0].deepCleanEstimate.discountApplied.savedAmount
                        )}
                      </span>
                    )}
                    <span className="price">{formatPrice(Object.values(estimates)[0].deepCleanEstimate.subtotal)}</span>
                    {Object.values(estimates)[0].deepCleanEstimate.discountApplied && (
                      <span className="discount-badge">
                        Save {Object.values(estimates)[0].deepCleanEstimate.discountApplied.type === 'percent' 
                          ? `${Object.values(estimates)[0].deepCleanEstimate.discountApplied.amount}%`
                          : formatPrice(Object.values(estimates)[0].deepCleanEstimate.discountApplied.savedAmount)
                        }
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Skipped Deep Clean Notice */}
            {skipDeepClean && (
              <div className="summary-block skipped-block">
                <div className="skipped-notice">
                  <AlertTriangle size={18} />
                  <div>
                    <strong>Deep Clean Skipped</strong>
                    <p>First visit will be regular recurring service</p>
                  </div>
                </div>
              </div>
            )}

            {/* Recurring Service Options */}
            {Object.entries(estimates).map(([freq, estimate]) => {
              if (estimate.requiresCustomQuote) {
                return (
                  <div key={freq} className="summary-block custom-quote-block">
                    <h4>{cleaningFrequencies[freq]?.label}</h4>
                    <p>Requires custom quote</p>
                  </div>
                );
              }

              return (
                <div key={freq} className="summary-block">
                  <h4>
                    <Calendar size={16} />
                    {cleaningFrequencies[freq]?.label} Service
                  </h4>
                  <div className="line-items">
                    {estimate.lineItems?.filter(item => item.type !== 'addon').map((item, idx) => (
                      <div key={idx} className={`line-item ${item.type}`}>
                        <span className="item-label">{item.label}</span>
                        <span className="item-desc">{item.description}</span>
                        <span className="item-price">
                          {item.price !== null ? formatPrice(item.price) : 'TBD'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="subtotal">
                    <span>
                      Per Visit
                      {estimate.priceOverride && (
                        <span className="override-badge">Override</span>
                      )}
                    </span>
                    <div className="price-with-discount">
                      {estimate.discountApplied && (
                        <span className="original-price">
                          {formatPrice(estimate.total + estimate.discountApplied.savedAmount)}
                        </span>
                      )}
                      <span className="price">{formatPrice(estimate.total)}</span>
                      {estimate.discountApplied && (
                        <span className="discount-badge">
                          Save {estimate.discountApplied.type === 'percent' 
                            ? `${estimate.discountApplied.amount}%`
                            : formatPrice(estimate.discountApplied.savedAmount)
                          }
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Warnings */}
            {requiresReview && (
              <div className="summary-warnings">
                <AlertTriangle size={18} />
                <div>
                  <strong>Manager Review Required</strong>
                  <p>This estimate needs approval before sending to client.</p>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Action Buttons - Always visible */}
      <div className="calculator-actions">
        <button 
          type="button" 
          className="btn btn-secondary"
          onClick={() => handleSave(false)}
          disabled={!canSave}
        >
          <Save size={18} />
          Save as Draft
        </button>
        <button 
          type="button" 
          className="btn btn-primary"
          onClick={() => handleSave(true)}
          disabled={!canSave}
        >
          <Eye size={18} />
          Save & Preview
        </button>
      </div>
    </div>
  );
}

export default EstimateCalculator;
